import React from 'react';
import Modal, { ModalProps } from '@app/component/overlays/modal/Modal';
import * as z from 'zod/v4';
import CompanyDepartmentInput from '@app/model/company/input/CompanyDepartmentInput';
import useAppForm from '@app/site/_form';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CompanyService from '@app/model/company/service/CompanyService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { CompanyQueryKeys } from '@app/model/company/service/CompanyGraphQLQueryKeys';
import CompanyDepartment from '@app/model/company/CompanyDepartment';


type CreateOrEditCompanyDepartmentModalProps = ModalProps & {
	department?: CompanyDepartment;
}

const CreateOrEditCompanyDepartmentModal = React.memo(
	function CreateOrEditCompanyDepartmentModal({
																								department,
																								...props
																							}: CreateOrEditCompanyDepartmentModalProps) {

		const toaster = useToaster();

		const CompanyDepartmentInputValidation = z.object({
			name: z.string().nonempty('Name is required'),
		});

		const defaultFormValues: CompanyDepartmentInput = {
			departmentId: department?department.id : undefined,
			name: department?department.name : '',
		};

		const queryClient = useQueryClient();

		const saveCompanyDepartmentMutation = useMutation({
			mutationFn: CompanyService.saveCompanyDepartment,
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.departments() }).then(() => {
					props.ref.current!.close();
					toaster.success('Company Department', 'Department saved successfully');
				});
			},
		});

		const form = useAppForm({
			defaultValues: defaultFormValues,
			validators: {
				onChange: CompanyDepartmentInputValidation,
			},
			onSubmit: ({ value }) => saveCompanyDepartmentMutation.mutateAsync(value).catch((e) => {
				form.setErrorMap({
					onSubmit: {
						fields: {
							name: {
								message: 'A department with this name already exists',
								isDirty: true,
							},
						},
					},
				});
			}),
		});

		return <form.AppForm>
			<form onSubmit={
				(e) => {
					e.preventDefault();
					e.stopPropagation();
				}
			}>
				<Modal {...props} title={department ? 'Edit Department' : 'Create Department'} actions={
					<SubmitButton state="primary">Save</SubmitButton>
				}>
					<form.AppField name="name"
												 children={(field) => <field.TextField label="Name"
																															 placeholder="Insert department name" />} />
				</Modal>
			</form>
		</form.AppForm>;

	});

export default CreateOrEditCompanyDepartmentModal;